import React from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import AuthLayout from "layouts/authLayout";
import HomeLayout from "layouts/homeLayout";
import BroadcastSendMessage from "pages/admin/broadcast/sendMessage";
import BroadcastList from "pages/admin/broadcastList/broadcastList";
import BroadcastListMessageView from "pages/admin/broadcastList/broadcastListMessageView";
import BroadcastListView from "pages/admin/broadcastList/broadcastListView";
import CallHistoryPage from "pages/admin/callHistory/callHistoryPage";
import CustomerPage from "pages/admin/customer/customerPage";
import DashboardPage from "pages/admin/dashboard/dashboardPage";
import MessageFuturePage from "pages/admin/message/messageFuture";
import MessageInReviewPage from "pages/admin/message/messageInReview";
import MessagePastPage from "pages/admin/message/messagePast";
import NoticeBoardPage from "pages/admin/notice/noticeBoard";
import AddCardPage from "pages/admin/payment/checkoutForm";
import PaymentPage from "pages/admin/payment/payment";
import WorkspaceSettingPage from "pages/admin/setting";
import SubscriptionPage from "pages/admin/subscriptions";
import UserProfilePage from "pages/admin/userProfile";
import WorkspacePage from "pages/admin/workspace/workspace";
import WorkspacesProfilePage from "pages/admin/workspaceProfile";
import ForgotPasswordPage from "pages/forgotPassword";
import LoginPage from "pages/login";
import BroadcastListPage from "pages/operator/broadcastList/broadcastList";
import BroadcastListOperatorView from "pages/operator/broadcastList/broadcastListView";
import OperatorCustomerPage from "pages/operator/customerPage";
import InboxPage from "pages/operator/inbox/inbox";
import UserSettingPage from "pages/operator/setting";
import ResetPasswordPage from "pages/resetPassword";
import SignUpPage from "pages/signUp";
import RequestQRCodePage from "pages/twoFactorAuthentication/requestQRCode";
import TwoFactorAuthenticationPage from "pages/twoFactorAuthentication/twoFactorAuthentication";
import VerifyAccountPage from "pages/verifyAccount";

import "antd/dist/reset.css";
import "App.css";
import "react-toastify/dist/ReactToastify.css";

import CardPage from "./pages/admin/card/cardPage";
import ChannelPage from "./pages/admin/channel/channelPage";
import CustomFields from "./pages/admin/customFields";
import InvoicesPage from "./pages/admin/invoices/invoicesPage";
import MessageTemplatePage from "./pages/admin/message/messageTemplates.js";
import NumberPage from "./pages/admin/numbers/number";
import UserOnlinePage from "./pages/admin/user/userOnlinePage";
import UserPage from "./pages/admin/user/userPage";
import { PERMISSION_TYPE } from "./constants";

import "assets/styles/index.scss";

const router = createBrowserRouter([
  {
    path: "/",
    Component: HomeLayout,
    children: [
      // admin module
      {
        path: "admin",
        children: [
          {
            id: "dashboard",
            path: "dashboard",
            index: true,
            handle: {
              permission: [
                PERMISSION_TYPE.SUPER_ADMIN,
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.FREE_USER,
              ],
            },
            Component: DashboardPage,
          },
          {
            id: "admin-user-profile",
            path: "user-profile",
            handle: {
              permission: [
                PERMISSION_TYPE.SUPER_ADMIN,
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.FREE_USER,
              ],
            },
            Component: UserProfilePage,
          },
          {
            id: "workspace-setting",
            path: "setting",
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
              ],
            },
            Component: WorkspaceSettingPage,
          },
          {
            id: "workspace-setting-detail",
            path: "setting/:workspaceId",
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
            Component: WorkspaceSettingPage,
          },
          {
            id: "workspace-profile",
            path: "workspace-profile",
            Component: WorkspacesProfilePage,
            handle: {
              permission: [PERMISSION_TYPE.WORKSPACE_ADMIN],
            },
          },
          {
            id: "notice-board",
            path: "notice",
            Component: NoticeBoardPage,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
              ],
            },
          },
          {
            id: "notice-board-detail",
            path: "notice/:workspaceId",
            Component: NoticeBoardPage,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
          },
          {
            id: "subscription",
            path: "subscriptions",
            Component: SubscriptionPage,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
          },
          {
            id: "subscription-detail",
            path: "subscriptions/:id",
            Component: SubscriptionPage,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
          },
          {
            id: "workspace",
            path: "workspaces",
            handle: {
              permission: [
                PERMISSION_TYPE.FREE_USER,
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
            Component: WorkspacePage,
          },
          {
            id: "user",
            path: "users/workspace/:id",
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
            Component: UserPage,
          },
          {
            id: "user-online",
            path: "users-online-now/workspace/:id",
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
            Component: UserOnlinePage,
          },
          {
            id: "message-template-past",
            path: "past",
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
            Component: MessagePastPage,
          },
          {
            id: "message-template-future",
            path: "future",
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
            Component: MessageFuturePage,
          },
          {
            id: "message-template-review",
            path: "review",
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
            Component: MessageInReviewPage,
          },
          {
            id: "message-template",
            path: "templates/:id",
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
            Component: MessageTemplatePage,
          },
          {
            id: "broadcast-list",
            path: "broadcast-list",
            Component: BroadcastList,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
          },
          {
            id: "broadcast-list-view",
            path: "broadcast-list/:id",
            Component: BroadcastListView,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
          },
          {
            id: "broadcast-list-view-message",
            path: "broadcast-list/messages/:id",
            Component: BroadcastListMessageView,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
          },
          {
            id: "custom-field",
            path: "custom-field",
            Component: CustomFields,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
          },
          {
            id: "customer",
            path: "customer/all-customer/:id",
            Component: CustomerPage,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
          },
          {
            id: "invoice",
            path: "invoices/:id",
            Component: InvoicesPage,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
          },
          {
            id: "service",
            path: "services/:id",
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
            Component: NumberPage,
          },
          {
            id: "channels",
            path: "channels",
            handle: {
              permission: [
                PERMISSION_TYPE.SUPER_ADMIN,
                PERMISSION_TYPE.WORKSPACE_ADMIN,
              ],
            },
            Component: ChannelPage,
          },
          {
            id: "add-card",
            path: "add-card/:id",
            handle: {
              permission: [PERMISSION_TYPE.WORKSPACE_ADMIN],
            },
            Component: AddCardPage,
          },
          {
            id: "card-management",
            path: "workspaces/:id/card",
            handle: {
              permission: [PERMISSION_TYPE.WORKSPACE_ADMIN],
            },
            Component: CardPage,
          },
          {
            id: "payment",
            path: "payment/:id",
            Component: PaymentPage,
          },
          {
            id: "callHistory",
            path: "call-history",
            Component: CallHistoryPage,
          },
        ],
      },
      // operator module
      {
        path: "",
        children: [
          {
            id: "user-inbox",
            path: "inbox/:id",
            Component: InboxPage,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
              ],
            },
          },
          {
            id: "user-profile",
            path: "profile",
            Component: UserProfilePage,
            handle: {
              permission: [
                PERMISSION_TYPE.FREE_USER,
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
                PERMISSION_TYPE.SUPER_ADMIN,
              ],
            },
          },
          {
            id: "user-setting",
            path: "setting",
            Component: UserSettingPage,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
              ],
            },
          },
          // {
          //   id: "user-setting-detail",
          //   path: "setting/:workspaceId",
          //   Component: UserSettingPage,
          //   handle: {
          //     permission: [
          //       PERMISSION_TYPE.WORKSPACE_ADMIN,
          //       PERMISSION_TYPE.SUPER_ADMIN,
          //     ],
          //   },
          // },
          {
            id: "user-send-message",
            path: "send-message",
            Component: BroadcastSendMessage,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
              ],
            },
          },
          {
            id: "user-broadcast-list",
            path: "broadcast-list",
            Component: BroadcastListPage,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
              ],
            },
          },
          {
            id: "user-broadcast-list-detail",
            path: "/broadcast-list/:id",
            Component: BroadcastListOperatorView,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
              ],
            },
          },
          {
            id: "user-customer",
            path: "customer/all-customer/:id",
            Component: OperatorCustomerPage,
            handle: {
              permission: [
                PERMISSION_TYPE.WORKSPACE_ADMIN,
                PERMISSION_TYPE.WORKSPACE_OPERATOR,
              ],
            },
          },
        ],
      },
    ],
  },
  {
    path: "auth",
    Component: AuthLayout,
    children: [
      { path: "sign-in", index: true, Component: LoginPage },
      { path: "sign-up", Component: SignUpPage },
      { path: "forgot-password", Component: ForgotPasswordPage },
      { path: "reset-password/:id", Component: ResetPasswordPage },
      { path: "verify-account", Component: VerifyAccountPage },
      { path: "invitation-user", Component: VerifyAccountPage },
      {
        path: "verify-two-factor-authentication",
        Component: TwoFactorAuthenticationPage,
      },
      { path: "request-qr-code", Component: RequestQRCodePage },
    ],
  },
  { path: "*", element: <Navigate to="/admin/dashboard" /> },
]);

function App() {
  return (
    <RouterProvider
      router={router}
      fallbackElement={<React.Suspense fallback={<div>Loading...</div>} />}
    />
  );
}

export default App;
